import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { ServiceSiteType } from 'api/api.types'
import { getActiveServiceGroups } from 'src/store/data.store'
import { setAppointmentServices } from 'src/store/appointment.store'
import { chunk } from 'utils/chunk'
import { Tab, Tabs } from 'components/Utility/Tabs/Tabs'
import { Slider } from 'components/Utility/Slider/Slider'
import { Arrow } from 'components/Utility/Arrow/Arrow'
import { ServiceInfo } from 'components/PageElements/ServiceInfo/ServiceInfo'
import AdditionalServicesActive from 'assets/services-icons-no-box/additional-active.svg'
import DesignsActive from 'assets/services-icons-no-box/designs-active.svg'
import HealthActive from 'assets/services-icons-no-box/health-active.svg'
import PedicureActive from 'assets/services-icons-no-box/pedicure-active.svg'
import PodologyActive from 'assets/services-icons-no-box/podology-active.svg'
import ManicureActive from 'assets/services-icons-no-box/manicure-active.svg'
import ComplexActive from 'assets/services-icons-no-box/complex-active.svg'
import BrowsActive from 'assets/services-icons-no-box/brows-active.svg'
import AdditionalServices from 'assets/services-icons-no-box/additional.svg'
import Designs from 'assets/services-icons-no-box/designs.svg'
import Health from 'assets/services-icons-no-box/health.svg'
import Pedicure from 'assets/services-icons-no-box/pedicure.svg'
import Podology from 'assets/services-icons-no-box/podology.svg'
import Complex from 'assets/services-icons-no-box/complex.svg'
import Manicure from 'assets/services-icons-no-box/manicure.svg'
import Brows from 'assets/services-icons-no-box/brows.svg'
import styles from './MainMenu.module.scss'

/** Sub menu ids below zero split a category by gender; positive ids are site service types */
const FOR_WOMEN = -1
const FOR_MEN = -2
/** The health category has no type of its own, only its sub menu types */
const HEALTH = 0

type MenuItem = {
  id: number,
  name: string,
  img: string,
  activeImg: string,
  title: string,
  subMenu?: Array<{ id: number, title: string }>,
}

const BY_GENDER = [
  { id: FOR_WOMEN, title: 'женщинам' },
  { id: FOR_MEN, title: 'мужчинам' },
]

const CATEGORIES: MenuItem[] = [
  { id: ServiceSiteType.complex, name: 'complex', img: Complex, activeImg: ComplexActive, title: 'комплекс', subMenu: BY_GENDER },
  { id: ServiceSiteType.manicure, name: 'manicure', img: Manicure, activeImg: ManicureActive, title: 'маникюр', subMenu: BY_GENDER },
  { id: ServiceSiteType.pedicure, name: 'pedicure', img: Pedicure, activeImg: PedicureActive, title: 'педикюр', subMenu: BY_GENDER },
  { id: ServiceSiteType.podology, name: 'podology', img: Podology, activeImg: PodologyActive, title: 'подология' },
  {
    id: HEALTH,
    name: 'health',
    img: Health,
    activeImg: HealthActive,
    title: 'здоровье и уход',
    subMenu: [
      { id: ServiceSiteType.complexCare, title: 'комплексный уход' },
      { id: ServiceSiteType.moisturizing, title: 'Увлажнение и питание' },
      { id: ServiceSiteType.strengthening, title: 'укрепление ногтей' },
      { id: ServiceSiteType.cleansing, title: 'очищение' },
    ],
  },
  { id: ServiceSiteType.designs, name: 'designs', img: Designs, activeImg: DesignsActive, title: 'дизайны' },
  {
    id: ServiceSiteType.additional,
    name: 'additionalServices',
    img: AdditionalServices,
    activeImg: AdditionalServicesActive,
    title: 'дополнительные услуги',
  },
  { id: ServiceSiteType.brows, name: 'brows', img: Brows, activeImg: BrowsActive, title: 'брови и ресницы' },
]

const CATEGORIES_SHORT: MenuItem[] = [
  { id: ServiceSiteType.manicure, name: 'manicure', img: Manicure, activeImg: ManicureActive, title: 'маникюр' },
  { id: ServiceSiteType.pedicure, name: 'pedicure', img: Pedicure, activeImg: PedicureActive, title: 'педикюр' },
  { id: ServiceSiteType.complex, name: 'complex', img: Complex, activeImg: ComplexActive, title: 'комплекс' },
]

const SERVICES_PER_PAGE = 3
const MOBILE_MAX_WIDTH = 428
const SLIDER_MIN_WIDTH = 1024 - 60
const SLIDER_MAX_WIDTH = 1200 - 60

type MainMenuProps = {
  defaultCategory?: number,
  onChangeCategory?: (categoryId: number) => void,
  /** Only manicure, pedicure and complex, without sub menus (salon and service pages) */
  onlyMainCategories?: boolean,
}

/** Service categories as tabs with a slider of service cards */
export const MainMenu: React.FC<MainMenuProps> = (props) => {
  const { defaultCategory = ServiceSiteType.complex, onChangeCategory, onlyMainCategories } = props
  const servicesContainerRef = useRef<HTMLDivElement>(null)
  const disableSlideTransition = useRef(false)
  const dispatch = useDispatch()
  const allGroups = useSelector(getActiveServiceGroups)
  const menuItems = onlyMainCategories ? CATEGORIES_SHORT : CATEGORIES
  const [activeMenuItemId, setActiveMenuItemId] = useState(defaultCategory)
  const [activeSubMenuItemId, setActiveSubMenuItemId] = useState(FOR_WOMEN)
  const [page, setPage] = useState(0)
  const [servicesWidth, setServicesWidth] = useState(0)
  const activeMenuItem = useMemo(
    () => menuItems.find((item) => item.id === activeMenuItemId),
    [menuItems, activeMenuItemId],
  )

  const handleMenuChange = useCallback((id: number) => {
    disableSlideTransition.current = true
    onChangeCategory?.(id)
    setPage(0)
    setActiveMenuItemId(id)
    const item = menuItems.find((menuItem) => menuItem.id === id)
    if (item?.subMenu) {
      setActiveSubMenuItemId(item.subMenu[0].id)
    }
    disableSlideTransition.current = false
  }, [onChangeCategory, menuItems])

  const handleSelectService = useCallback((serviceId: number) => {
    dispatch(setAppointmentServices({ clientIndex: 0, services: [{ id: serviceId, amount: 1 }] }))
  }, [dispatch])

  const handleSubMenuChange = useCallback((id: number) => {
    disableSlideTransition.current = true
    setPage(0)
    setActiveSubMenuItemId(id)
    disableSlideTransition.current = false
  }, [])

  const activeGroups = useMemo(() => allGroups.filter((group) => {
    const vars = group.page.templateVars?.pageVars
    if (!vars || !activeMenuItem) {
      return false
    }
    if (activeMenuItem.subMenu) {
      if (activeSubMenuItemId === FOR_MEN) {
        return vars.type === activeMenuItem.id && !!vars.forMen
      } else if (activeSubMenuItemId === FOR_WOMEN) {
        return vars.type === activeMenuItem.id && !vars.forMen
      }
      return vars.type === activeSubMenuItemId
    }
    return vars.type === activeMenuItem.id
  }), [allGroups, activeMenuItem, activeSubMenuItemId])

  const activeServicesChunks = useMemo(() => chunk(activeGroups, SERVICES_PER_PAGE), [activeGroups])

  const activeServicesPages = useMemo(() => activeServicesChunks.map((pageGroups, index) => (
    <div key={index} className={styles.servicesPage}>
      {pageGroups.map((group) => (
        <div key={group.page.id} className={styles.servicesContainer}>
          <ServiceInfo className={styles.serviceInfo} group={group} onSelect={handleSelectService} />
        </div>
      ))}
    </div>
  )), [activeServicesChunks, handleSelectService])

  const isFirstPage = page === 0
  const isLastPage = page >= activeServicesChunks.length - 1

  const prevPage = useCallback(() => {
    if (!isFirstPage) {
      setPage((current) => current - 1)
    }
  }, [isFirstPage])

  const nextPage = useCallback(() => {
    if (!isLastPage) {
      setPage((current) => current + 1)
    }
  }, [isLastPage])

  useEffect(() => {
    const container = servicesContainerRef.current
    if (!container) {
      return undefined
    }
    setServicesWidth(container.clientWidth)
    const handleWindowResize = () => {
      Promise.resolve().then(() => {
        if (servicesContainerRef.current) {
          setServicesWidth(servicesContainerRef.current.clientWidth)
        }
      })
    }
    window.addEventListener('resize', handleWindowResize, { passive: true })
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [])

  const onMobile = servicesWidth <= MOBILE_MAX_WIDTH
  const servicesSliderWidth = onMobile
    ? servicesWidth
    : Math.min(Math.max(servicesWidth, SLIDER_MIN_WIDTH), SLIDER_MAX_WIDTH)

  return (
    <div className={styles.container} ref={servicesContainerRef} data-block={'services'}>
      <Tabs selected={activeMenuItemId} onChange={handleMenuChange} tabsClassName={styles.mainMenuBox}>
        {menuItems.map((value) => {
          const imageSrc = value.id === activeMenuItemId ? value.activeImg : value.img
          return <Tab selectedClassName={styles.active} className={styles.menuBox} key={value.id} value={value.id}>
            <div className={styles.menuItem}>
              {imageSrc
                ? <img src={imageSrc} className={styles.imgContainer} alt={value.title} />
                : <div className={styles.imgPlaceholder} />}
              <span className={styles.menuTitle}>{value.title}</span>
            </div>
          </Tab>
        })}
      </Tabs>

      {!!activeMenuItem?.subMenu && <div className={styles.subMenuContainer}>
        {activeMenuItem.subMenu.map((value, index) => (
          <div className={styles.contentBox} key={index} onClick={() => handleSubMenuChange(value.id)}>
            <span className={clsx({
              [styles.contentText]: true,
              [styles.contentTextHover]: activeSubMenuItemId === value.id,
            })}>
              {value.title}
            </span>
          </div>
        ))}
      </div>}

      <div className={styles.menuWrapper}>
        <div className={styles.arrows}>
          {!isFirstPage && <Arrow onClick={prevPage} className={styles.arrowLeft} bigOne direction={'left'} />}
          {!isLastPage && <Arrow onClick={nextPage} className={styles.arrowRight} bigOne direction={'right'} />}
        </div>
        <div className={styles.menuContainer}>
          {onMobile
            ? activeServicesPages
            : (
              <Slider
                itemWidth={servicesSliderWidth}
                page={page}
                onPageChange={setPage}
                disableTransition={disableSlideTransition.current}
              >
                {activeServicesPages}
              </Slider>
            )}
        </div>
      </div>
    </div>
  )
}
